// frozen_string_literal: true

/**
 * 共享的图片生成辅助函数
 * 用于 mark_result_chart_controller 和 batch_chart_generator_controller
 */

/**
 * 创建图片数据项
 * @param {Object} data - 包含任务信息的数据对象
 * @returns {Array} 信息项数组
 */
export function createInfoItems(data) {
  return [
    { label: "任务信息", value: "", isTitle: true },
    { label: "租户ID", value: "268ef75a" },
    { label: "任务名称", value: data.taskName || "N/A" },
    { label: "任务ID", value: data.taskId || "N/A" },
    { label: "子任务ID", value: data.subTaskId || "N/A" },
    { label: "当前markitemID", value: data.markItemId || "N/A" },
  ];
}

/**
 * 计算 canvas 所需的最大宽度
 * @param {CanvasRenderingContext2D} ctx - Canvas 上下文
 * @param {Array} infoItems - 信息项数组
 * @returns {number} 最大宽度（像素）
 */
export function calculateMaxWidth(ctx, infoItems) {
  let maxWidth = 200; // 最小宽度

  const boldFont =
    'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  const regularFont =
    '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  infoItems.forEach((item) => {
    ctx.font = boldFont;
    const labelWidth = ctx.measureText(`${item.label}：`).width;

    ctx.font = regularFont;
    const valueWidth = ctx.measureText(item.value).width;

    const totalWidth = labelWidth + valueWidth + 20; // 10px padding on each side
    if (totalWidth > maxWidth) maxWidth = totalWidth;
  });

  return maxWidth;
}

/**
 * 绘制标题行（带复制图标）
 * @param {CanvasRenderingContext2D} ctx - Canvas 上下文
 * @param {string} label - 标题文本
 * @param {number} x - X 坐标
 * @param {number} y - Y 坐标（baseline）
 */
export function drawTitleRow(ctx, label, x, y) {
  ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
  ctx.font =
    'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  ctx.fillText(label, x, y);

  // 绘制复制图标（SVG）
  const iconX = x + ctx.measureText(label).width + 8;
  const iconY = y - 12;
  const iconSize = 14;

  ctx.save();
  ctx.translate(iconX, iconY);

  // SVG viewBox="64 64 896 896"，缩放到 14x14
  const scale = iconSize / 832; // 896 - 64 = 832
  ctx.scale(scale, scale);
  ctx.translate(-64, -64);

  ctx.fillStyle = "#1890ff";
  ctx.strokeStyle = "#1890ff";
  ctx.lineWidth = 1 / scale;

  // 绘制 SVG 路径
  const copyPath = new Path2D(
    "M832 64H296c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h496v688c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V96c0-17.7-14.3-32-32-32zM704 192H192c-17.7 0-32 14.3-32 32v530.7c0 8.5 3.4 16.6 9.4 22.6l173.3 173.3c2.2 2.2 4.7 4 7.4 5.5v1.9h4.2c3.5 1.3 7.2 2 11 2H704c17.7 0 32-14.3 32-32V224c0-17.7-14.3-32-32-32zM350 856.2L263.9 770H350v86.2zM664 888H414V746c0-22.1-17.9-40-40-40H232V264h432v624z",
  );
  ctx.fill(copyPath);

  ctx.restore();
}

/**
 * 绘制普通信息行
 * @param {CanvasRenderingContext2D} ctx - Canvas 上下文
 * @param {string} label - 标签文本
 * @param {string} value - 值文本
 * @param {number} x - X 坐标
 * @param {number} y - Y 坐标（baseline）
 */
export function drawInfoRow(ctx, label, value, x, y) {
  // 绘制标签（粗体）
  ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
  ctx.font =
    'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  ctx.fillText(`${label}：`, x, y);

  // 绘制值（常规）
  ctx.fillStyle = "rgba(0, 0, 0, 0.65)";
  ctx.font =
    '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  const labelWidth = ctx.measureText(`${label}：`).width;
  ctx.fillText(value, x + labelWidth, y);
}

/**
 * 生成图片 Canvas
 * @param {Object} data - 任务数据对象
 * @returns {HTMLCanvasElement} 生成的 canvas 元素
 */
export function generateChartCanvas(data) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // 准备信息项
  const infoItems = createInfoItems(data);

  // 设置字体用于计算
  ctx.font =
    'bold 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // 计算尺寸
  const maxWidth = calculateMaxWidth(ctx, infoItems);
  const lineHeight = 28;
  const paddingY = 10;
  const firstLineBaselineOffset = 12;
  const lastLineBottomSpace = 14;

  canvas.width = Math.ceil(maxWidth);
  canvas.height =
    paddingY +
    firstLineBaselineOffset +
    (infoItems.length - 1) * lineHeight +
    lastLineBottomSpace;

  // 绘制背景
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // 绘制内容
  let yPos = paddingY + firstLineBaselineOffset;
  infoItems.forEach((item) => {
    if (item.isTitle) {
      drawTitleRow(ctx, item.label, 10, yPos);
    } else {
      drawInfoRow(ctx, item.label, item.value, 10, yPos);
    }
    yPos += lineHeight;
  });

  return canvas;
}

/**
 * 上传图片图片
 * @param {string} markResultId - MarkResult ID
 * @param {Blob} blob - 图片 Blob
 * @returns {Promise<Response>} Fetch 响应
 */
export async function uploadChartImage(markResultId, blob) {
  const formData = new FormData();
  formData.append("chart_image", blob, `mark_result_${markResultId}_chart.png`);

  const response = await fetch(`/mark_results/${markResultId}/attach_chart`, {
    method: "POST",
    headers: {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`上传失败: ${response.status}`);
  }

  return response;
}

/**
 * Canvas 转 Blob 的 Promise 封装
 * @param {HTMLCanvasElement} canvas - Canvas 元素
 * @returns {Promise<Blob>} 图片 Blob
 */
export function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Canvas to Blob conversion failed"));
      }
    }, "image/png");
  });
}
